import { createTheme, IEffects, IPartialTheme, ITheme } from '@fluentui/react';
import { useMediaMatch } from 'common/hooks/useMediaMatch';
import { isThemeSwitchingWebEnabled, isThemeSwitchingWebViewEnabled } from 'common/utilities/FlightSetting';
import { useWebView } from 'store/webView/useWebView';
import { isIOS, isWindows } from 'store/webView/webViewHelpers';

import { AppFontStyles } from './AppFontStyles';
import { AppSemanticColors } from './AppSemanticColors';
import { DarkTheme } from './Dark';
import { LightTheme } from './Light';
import { ThemeType } from './ThemeTypes';

export interface IAppEffects extends IEffects {
  elevation2?: string;
  elevation28?: string;
}

export interface IAppTheme extends ITheme {
  // can't make it non optional due to issue with makeStyles function.
  appColors?: AppSemanticColors;
  appFonts?: AppFontStyles;
  effects: IAppEffects;
}

export interface IAppPartialTheme extends IPartialTheme {
  // Parameters below should be partial. But I'm having issue merging default values and overrides here.
  // Since default values are undefined at runtime even when they properly imported.
  appColors?: AppSemanticColors;
  appFonts?: AppFontStyles;
  effects?: Partial<IAppEffects>;
}

export function createAppTheme(theme: IAppPartialTheme): IAppTheme {
  const fullTheme = createTheme(theme);
  const appTheme: IAppTheme = {
    ...fullTheme,
    appColors: theme?.appColors,
    appFonts: theme?.appFonts,
  };

  return appTheme;
}

export const isPrefersColorSchemeSupported = () =>
  window.matchMedia('(prefers-color-scheme: dark)').matches || window.matchMedia('(prefers-color-scheme: light)').matches;

/**
 * Get the color scheme style rule for the given theme name.
 * Use this to set the color scheme (for default scrollbar color) at the root level.
 * @param themeName {ThemeType}
 */
export function getDefaultScrollbarColorSchemeStyle(themeName: ThemeType): { colorScheme: 'dark' | 'light' } {
  return {
    colorScheme: themeName === ThemeType.Dark ? 'dark' : 'light',
  };
}

/**
 * Determine whether a user on Web prefers dark theme.
 *
 * @returns {boolean} true if dark theme is set at the OS level
 */
export function usePrefersDarkColorScheme(): boolean {
  // https://developer.mozilla.org/en-US/docs/Web/CSS/@media/prefers-color-scheme
  const prefersDarkTheme = useMediaMatch('(prefers-color-scheme: dark)');
  return prefersDarkTheme;
}

/**
 * Returns the current theme type after determining the user's preference and
 * also whether or not the WebView platform and version supports dark theme.
 *
 * @returns {ThemeType} Current theme
 */
export function useCurrentThemeType(): ThemeType {
  const {
    isWebView,
    theme: webViewInitialNativeAppStateTheme,
    supportsDarkMode: nativeAppSupportsDarkMode,
    webViewPlatform,
  } = useWebView();
  const userPrefersDarkThemeInOsSettings = usePrefersDarkColorScheme();

  // WebView
  if (isThemeSwitchingWebViewEnabled() && isWebView && nativeAppSupportsDarkMode) {
    if (isWindows(webViewPlatform) || isIOS(webViewPlatform)) {
      // For some platforms, check if the user prefers dark theme in OS settings:
      return userPrefersDarkThemeInOsSettings ? ThemeType.Dark : ThemeType.Light;
    }
    // For other platforms, only rely on initial native app state to determine theme:
    return webViewInitialNativeAppStateTheme === ThemeType.Dark ? ThemeType.Dark : ThemeType.Light;
  }

  // Web
  if (isThemeSwitchingWebEnabled() && !isWebView) {
    return userPrefersDarkThemeInOsSettings ? ThemeType.Dark : ThemeType.Light;
  }

  // Default to light theme:
  return ThemeType.Light;
}

export function useAppTheme(): IAppTheme {
  const { webViewPlatform } = useWebView();
  const theme = useCurrentThemeType();

  return theme === ThemeType.Dark ? DarkTheme(webViewPlatform) : LightTheme(webViewPlatform);
}
